/**
 * Post-process rendered HTML to auto-embed media URLs as native players.
 * Runs AFTER linkifyFilePaths() in the markdown pipeline.
 *
 * Detects:
 * - Direct video: .mp4, .webm, .mov, .ogg (video)
 * - Direct audio: .mp3, .wav, .ogg (audio), .m4a
 * - YouTube: youtube.com/watch?v=, youtu.be/
 * - Vimeo: vimeo.com/
 */

const VIDEO_EXT_RE = /\.(mp4|webm|mov)$/i;
const AUDIO_EXT_RE = /\.(mp3|wav|ogg|m4a|flac)$/i;
const YOUTUBE_RE = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})(?:[&?][\w=&%-]*)*/;
const VIMEO_RE = /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/;

function createVideoEmbed(url: string): string {
  return `<div class="media-embed video-embed"><video controls preload="metadata" playsinline><source src="${escapeHtml(url)}"></video></div>`;
}

function createAudioEmbed(url: string): string {
  return `<div class="media-embed audio-embed"><audio controls preload="metadata"><source src="${escapeHtml(url)}"></audio></div>`;
}

function createYouTubeEmbed(videoId: string): string {
  return `<div class="media-embed video-embed youtube-embed"><iframe src="https://www.youtube-nocookie.com/embed/${escapeHtml(videoId)}" allow="autoplay; encrypted-media" allowfullscreen loading="lazy" style="border:0;width:100%;aspect-ratio:16/9;border-radius:8px;"></iframe></div>`;
}

function createVimeoEmbed(videoId: string): string {
  return `<div class="media-embed video-embed vimeo-embed"><iframe src="https://player.vimeo.com/video/${escapeHtml(videoId)}" allow="autoplay; fullscreen" allowfullscreen loading="lazy" style="border:0;width:100%;aspect-ratio:16/9;border-radius:8px;"></iframe></div>`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Process rendered HTML to replace media links with inline players.
 * Only replaces links (<a> tags) that point to media URLs — doesn't touch plain text URLs.
 */
export function embedMedia(html: string): string {
  // Quick bail-out
  if (!html.includes('<a ')) return html;

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild!;
  const links = root.querySelectorAll('a');

  for (const link of Array.from(links)) {
    const href = link.getAttribute('href') || '';
    if (!href) continue;

    // Skip file-links (handled by Code Viewer)
    if (link.classList.contains('file-link')) continue;

    // Skip links inside <pre> / code blocks
    if (link.closest('pre')) continue;

    let embedHtml: string | null = null;

    // YouTube
    const ytMatch = href.match(YOUTUBE_RE);
    if (ytMatch) {
      embedHtml = createYouTubeEmbed(ytMatch[1]);
    }

    // Vimeo
    if (!embedHtml) {
      const vimeoMatch = href.match(VIMEO_RE);
      if (vimeoMatch) {
        embedHtml = createVimeoEmbed(vimeoMatch[1]);
      }
    }

    // Direct video
    if (!embedHtml && VIDEO_EXT_RE.test(href)) {
      embedHtml = createVideoEmbed(href);
    }

    // Direct audio
    if (!embedHtml && AUDIO_EXT_RE.test(href)) {
      embedHtml = createAudioEmbed(href);
    }

    if (embedHtml) {
      const wrapper = doc.createElement('div');
      wrapper.innerHTML = embedHtml;
      // Keep the original link as a fallback below the embed
      const fallback = doc.createElement('div');
      fallback.className = 'media-fallback-link';
      fallback.innerHTML = `<a href="${escapeHtml(href)}" target="_blank" rel="noopener">${link.textContent || href}</a>`;
      wrapper.firstElementChild!.appendChild(fallback);
      link.replaceWith(wrapper.firstElementChild!);
    }
  }

  return root.innerHTML;
}
